import { useCallback } from 'react';
import { useAuth } from '@/hooks/use-auth';
import {
  Announcement,
  AnnouncementWithReadStatus,
} from '@/types/announcements';

import { apiRequest } from '@/lib/queryClient';

/**
 * A service for managing in-app announcements
 * Uses server API exclusively (no local storage fallback)
 */
export function useAnnouncements() {
  const { user } = useAuth();
  
  /**
   * Get all announcements from API (admin only)
   */
  const getAllAnnouncements = useCallback(async (): Promise<Announcement[]> => {
    try {
      const response = await apiRequest('GET', '/api/announcements');
      if (!response.ok) {
        throw new Error(`API returned status ${response.status}`);
      }
      
      const data = await response.json();
      return data.data || [];
    } catch (error) {
      console.error('Failed to get announcements from API:', error);
      return [];
    }
  }, []);

  /**
   * Get announcements for the current user based on membership
   */
  const getAnnouncementsForCurrentUser = useCallback(async (): Promise<AnnouncementWithReadStatus[]> => {
    if (!user) return [];

    try {
      const response = await apiRequest('GET', '/api/announcements/user');
      
      if (!response.ok) {
        throw new Error(`API returned status ${response.status}`);
      }
      
      const result = await response.json();
      
      // Check if we have a valid response with success flag and data array
      if (result && result.success && Array.isArray(result.data)) {
        // Map each announcement to include isVisible flag
        return result.data.map((announcement: any) => ({
          ...announcement,
          // Add isVisible property
          isVisible: true,
          // Map the read status to the expected format
          readStatus: {
            announcementId: announcement.id,
            read: announcement.read || false,
            permanentlyDismissed: announcement.permanentlyDismissed || false,
            dismissCount: announcement.dismissCount || 0,
            dismissedUntil: announcement.dismissedUntil || null
          }
        }));
      }
      
      console.error('Invalid response format from API:', result);
      return [];
    } catch (error) {
      console.error('Failed to get announcements from API:', error);
      return [];
    }
  }, [user]);

  /**
   * Create a new announcement
   */
  const createAnnouncement = useCallback(async (announcement: Omit<Announcement, 'id' | 'createdAt' | 'updatedAt'>): Promise<Announcement> => {
    try {
      // Format dates as ISO strings if they are Date objects
      const formattedAnnouncement = {
        ...announcement,
        startDate: announcement.startDate instanceof Date ? announcement.startDate.toISOString() : announcement.startDate,
        endDate: announcement.endDate instanceof Date ? announcement.endDate.toISOString() : announcement.endDate
      };
      
      const response = await apiRequest('POST', '/api/announcements', formattedAnnouncement);
      
      if (!response.ok) {
        throw new Error(`API returned status ${response.status}`);
      }
      
      const data = await response.json();
      return data.data;
    } catch (error) {
      console.error('Failed to create announcement via API:', error);
      throw error;
    }
  }, []);

  /**
   * Update an existing announcement
   */
  const updateAnnouncement = useCallback(async (id: string, data: Partial<Announcement>): Promise<Announcement> => {
    try {
      const response = await apiRequest('PUT', `/api/announcements/${id}`, data);
      
      if (!response.ok) {
        throw new Error(`API returned status ${response.status}`);
      }
      
      const responseData = await response.json();
      return responseData.data;
    } catch (error) {
      console.error(`Failed to update announcement ${id} via API:`, error);
      throw error;
    }
  }, []);

  /**
   * Delete an announcement
   */
  const deleteAnnouncement = useCallback(async (id: string): Promise<void> => {
    try {
      const response = await apiRequest('DELETE', `/api/announcements/${id}`);
      
      if (!response.ok) {
        throw new Error(`API returned status ${response.status}`);
      }
    } catch (error) {
      console.error(`Failed to delete announcement ${id} via API:`, error);
      throw error;
    }
  }, []);

  /**
   * Delete all announcements
   */
  const clearAllAnnouncements = useCallback(async (): Promise<void> => {
    try {
      const response = await apiRequest('DELETE', '/api/announcements');
      
      if (!response.ok) {
        throw new Error(`API returned status ${response.status}`);
      }
    } catch (error) {
      console.error('Failed to clear all announcements via API:', error);
      throw error;
    }
  }, []);

  /**
   * Mark an announcement as read
   */
  const markAsRead = useCallback(async (announcementId: string): Promise<void> => {
    if (!user) return;
    
    try {
      const response = await apiRequest('POST', `/api/announcements/${announcementId}/read`);
      
      if (!response.ok) {
        throw new Error(`API returned status ${response.status}`);
      }
    } catch (error) {
      console.error(`Failed to mark announcement ${announcementId} as read via API:`, error);
      throw error;
    }
  }, [user]);

  /**
   * Dismiss an announcement temporarily for a number of hours
   */
  const dismissTemporarily = useCallback(async (announcementId: string, hours: number = 24): Promise<void> => {
    if (!user) return;
    
    try {
      const response = await apiRequest('POST', `/api/announcements/${announcementId}/dismiss`, { 
        permanent: false,
        dismissUntil: hours 
      });
      
      if (!response.ok) {
        const error = await response.json();
        if (error.maxDismissCount) {
          throw new Error(`Maximum dismiss count (${error.maxDismissCount}) reached`);
        }
        throw new Error(`API returned status ${response.status}`);
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error(`Failed to temporarily dismiss announcement ${announcementId} via API:`, error);
      throw error;
    }
  }, [user]);

  /**
   * Dismiss an announcement permanently
   */
  const dismissPermanently = useCallback(async (announcementId: string): Promise<void> => {
    if (!user) return;
    
    try {
      const response = await apiRequest('POST', `/api/announcements/${announcementId}/dismiss`, { permanent: true });
      
      if (!response.ok) {
        const error = await response.json();
        if (error.maxDismissCount) {
          throw new Error(`Maximum dismiss count (${error.maxDismissCount}) reached`);
        }
        throw new Error(`API returned status ${response.status}`);
      }
    } catch (error) {
      console.error(`Failed to permanently dismiss announcement ${announcementId} via API:`, error);
      throw error;
    }
  }, [user]);

  /**
   * Import announcements from JSON
   */
  const importAnnouncements = useCallback(async (importedAnnouncements: Announcement[]): Promise<void> => {
    try {
      const response = await apiRequest('POST', '/api/announcements/import', { announcements: importedAnnouncements });
      
      if (!response.ok) {
        throw new Error(`API returned status ${response.status}`);
      }
    } catch (error) {
      console.error('Failed to import announcements via API:', error);
      throw error;
    }
  }, []);

  /**
   * Export announcements as JSON
   */
  const exportAnnouncements = useCallback(async (): Promise<string> => {
    try {
      const response = await apiRequest('GET', '/api/announcements/export');
      
      if (!response.ok) {
        throw new Error(`API returned status ${response.status}`);
      }
      
      const data = await response.json();
      return JSON.stringify(data, null, 2);
    } catch (error) {
      console.error('Failed to export announcements via API:', error);
      throw error;
    }
  }, []);

  return {
    getAllAnnouncements,
    getAnnouncementsForCurrentUser,
    createAnnouncement,
    updateAnnouncement,
    deleteAnnouncement,
    clearAllAnnouncements,
    markAsRead,
    dismissTemporarily,
    dismissPermanently,
    importAnnouncements,
    exportAnnouncements,
  };
}